/**
 * Climate-graph data and axes.
 *
 * A climate graph (a Walter–Lieth style climate diagram, as geography courses draw it)
 * shows twelve months on one set of axes: mean monthly temperature as a line against the
 * left axis, and total monthly precipitation as bars against the right axis. The two
 * axes share their gridlines — and their zero line, so bars grow from the 0 °C level
 * even in a cold climate — which means the right axis is a multiple of the left one.
 *
 * In Walter and Lieth's convention that multiple is fixed at 10 °C = 20 mm, so wherever
 * the line runs above the bars the month is dry (evaporation exceeds rainfall); students'
 * graphs usually let the multiple float to fit the data.
 */

import { parseLabeledRows } from "@/lib/tool-kit/charts/parse";
import { niceTicks } from "@/lib/tool-kit/charts/scale";

export type TemperatureUnit = "C" | "F";
export type PrecipitationUnit = "mm" | "in";

export const MM_PER_INCH = 25.4;

export function convertTemperature(
  value: number,
  from: TemperatureUnit,
  to: TemperatureUnit
): number {
  if (from === to) {
    return value;
  }
  return from === "C" ? (value * 9) / 5 + 32 : ((value - 32) * 5) / 9;
}

export function convertPrecipitation(
  value: number,
  from: PrecipitationUnit,
  to: PrecipitationUnit
): number {
  if (from === to) {
    return value;
  }
  return from === "mm" ? value / MM_PER_INCH : value * MM_PER_INCH;
}

export interface ClimateMonth {
  /** The month's name as typed, or null when the line had none. */
  label: string | null;
  temperature: number | null;
  precipitation: number | null;
}

export interface ClimateTable {
  months: ClimateMonth[];
  /** Line numbers that held no usable number and were left out. */
  skipped: number[];
  /** Cells that were not numbers. */
  invalidCells: number;
}

/**
 * Rows of `Month, temperature, precipitation`. The month name may be left off
 * (`4.5, 55`), and a first line without numbers is a header.
 */
export function parseClimateTable(text: string): ClimateTable {
  const table = parseLabeledRows(text);
  const unlabelled =
    table.seriesCount === 1 &&
    table.rows.length > 0 &&
    table.rows.every(
      (row) =>
        !Number.isNaN(Number(row.label.replace(",", "."))) &&
        row.label.trim() !== ""
    );
  const months: ClimateMonth[] = table.rows.map((row) => {
    if (unlabelled) {
      return {
        label: null,
        temperature: Number(row.label.replace(",", ".")),
        precipitation: row.values[0] ?? null,
      };
    }
    return {
      label: row.label.trim() === "" ? null : row.label.trim(),
      temperature: row.values[0] ?? null,
      precipitation: row.values[1] ?? null,
    };
  });
  return { months, skipped: table.skipped, invalidCells: table.invalidCells };
}

export interface ClimateStats {
  meanTemperature: number;
  totalPrecipitation: number;
  warmest: number;
  coldest: number;
  wettest: number;
  driest: number;
  /** Warmest minus coldest month. */
  range: number;
}

/** Annual statistics from complete months (index into the arrays). Null for no data. */
export function climateStats(
  temperatures: readonly number[],
  precipitations: readonly number[]
): ClimateStats | null {
  if (temperatures.length === 0 || precipitations.length === 0) {
    return null;
  }
  const argMax = (values: readonly number[]) =>
    values.indexOf(Math.max(...values));
  const argMin = (values: readonly number[]) =>
    values.indexOf(Math.min(...values));
  const warmest = argMax(temperatures);
  const coldest = argMin(temperatures);
  return {
    meanTemperature:
      temperatures.reduce((sum, value) => sum + value, 0) / temperatures.length,
    totalPrecipitation: precipitations.reduce((sum, value) => sum + value, 0),
    warmest,
    coldest,
    wettest: argMax(precipitations),
    driest: argMin(precipitations),
    range: temperatures[warmest] - temperatures[coldest],
  };
}

export interface ClimateAxes {
  tempMin: number;
  tempMax: number;
  tempStep: number;
  precipMin: number;
  precipMax: number;
  precipStep: number;
  /** Intervals above and below the zero line, shared by both axes. */
  up: number;
  down: number;
}

const NICE_MANTISSAS = [1, 2, 5, 10];

/** The smallest 1–2–5 × 10ᵏ step that is at least `minimum`. */
export function niceStepAtLeast(minimum: number): number {
  if (!(minimum > 0 && Number.isFinite(minimum))) {
    return 1;
  }
  const power = Math.floor(Math.log10(minimum));
  for (const exponent of [power, power + 1]) {
    for (const mantissa of NICE_MANTISSAS) {
      const step = mantissa * 10 ** exponent;
      if (step >= minimum * (1 - 1e-9)) {
        return Number(step.toPrecision(12));
      }
    }
  }
  return 10 ** (power + 2);
}

/**
 * Axes for temperature (left) and precipitation (right) that share gridlines and the
 * zero line. With `linked`, the right axis runs at two units of precipitation per unit of
 * temperature (10 °C : 20 mm), and the left axis grows until the bars fit.
 */
export function climateAxes(
  temperatures: readonly number[],
  precipitations: readonly number[],
  linked: boolean
): ClimateAxes {
  const lowest = Math.min(0, ...temperatures);
  const highest = Math.max(0, ...temperatures);
  const wettest = Math.max(0, ...precipitations);
  const ticks = niceTicks(lowest, highest, { includeZero: true, count: 5 });
  const tempStep = ticks.step;
  let up = Math.max(1, Math.round(ticks.max / tempStep));
  const down = Math.round(-ticks.min / tempStep);
  let precipStep: number;
  if (linked) {
    precipStep = 2 * tempStep;
    while (up * precipStep < wettest) {
      up += 1;
    }
  } else {
    precipStep = niceStepAtLeast(wettest / up);
  }
  return {
    tempMin: -down * tempStep,
    tempMax: up * tempStep,
    tempStep,
    precipMin: -down * precipStep,
    precipMax: up * precipStep,
    precipStep,
    up,
    down,
  };
}
